import { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import LinearProgress from "@mui/material/LinearProgress";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import type { SelectChangeEvent } from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useQuery } from "@tanstack/react-query";
import { THIRD_PARTY_CONTRACT_NETWORK_TYPES } from "../../constants/pdm";
import { getAvailableNetworks } from "../../services/deploymentsManagement.service";
import { ThirdPartyContractDetailsDialog } from "./ThirdPartyContractDetailsDialog";
import { useThirdPartyContractsList } from "./useThirdPartyContractsList";

export default function ThirdPartyContractsView() {
  const [draftNetwork, setDraftNetwork] = useState("");
  const [draftNetworkType, setDraftNetworkType] = useState("");
  const [appliedNetwork, setAppliedNetwork] = useState("");
  const [appliedNetworkType, setAppliedNetworkType] = useState("");
  const [selectedContractId, setSelectedContractId] = useState<string | null>(null);
  const detailsDialogOpen = Boolean(selectedContractId);

  const applied = useMemo(
    () => ({ network: appliedNetwork, networkType: appliedNetworkType }),
    [appliedNetwork, appliedNetworkType],
  );

  const contractsQuery = useThirdPartyContractsList(applied);

  const networksQuery = useQuery({
    queryKey: ["available-networks", "third-party-contracts"],
    queryFn: getAvailableNetworks,
  });

  const networkOptions = useMemo(
    () => (networksQuery.data ?? []).map((n) => n.id),
    [networksQuery.data],
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setAppliedNetwork(draftNetwork);
    setAppliedNetworkType(draftNetworkType);
  };

  const rows = contractsQuery.data ?? [];
  const showListProgress = contractsQuery.isPending || contractsQuery.isFetching;

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ color: "brand.main" }}>
        Third party contracts
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Preset third-party contract addresses by network.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%" }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            sx={{ alignItems: { md: "center" } }}
          >
            <FormControl size="small" fullWidth>
              <InputLabel id="tpc-network-label">Network</InputLabel>
              <Select
                labelId="tpc-network-label"
                label="Network"
                value={draftNetwork}
                onChange={(e: SelectChangeEvent) => {
                  setDraftNetwork(e.target.value);
                }}
              >
                <MenuItem value="">All</MenuItem>
                {networksQuery.isLoading && (
                  <MenuItem value="" disabled>
                    Loading…
                  </MenuItem>
                )}
                {networksQuery.isError && (
                  <MenuItem value="" disabled>
                    Unable to load networks
                  </MenuItem>
                )}
                {networkOptions.map((n) => (
                  <MenuItem key={n} value={n}>
                    {n}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel id="tpc-network-type-label">Network type</InputLabel>
              <Select
                labelId="tpc-network-type-label"
                label="Network type"
                value={draftNetworkType}
                onChange={(e: SelectChangeEvent) => {
                  setDraftNetworkType(e.target.value);
                }}
              >
                <MenuItem value="">All</MenuItem>
                {THIRD_PARTY_CONTRACT_NETWORK_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              sx={{ whiteSpace: "nowrap" }}
            >
              Search
            </Button>
          </Stack>
        </Box>
      </Paper>

      <Box sx={{ position: "relative" }}>
        {showListProgress ? (
          <LinearProgress
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              top: 0,
              zIndex: 1,
              borderTopLeftRadius: 1,
              borderTopRightRadius: 1,
            }}
          />
        ) : null}
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>DSApps</TableCell>
                <TableCell>Network</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Address</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.protocolName}</TableCell>
                  <TableCell>{row.network}</TableCell>
                  <TableCell>{row.networkType}</TableCell>
                  <TableCell
                    sx={{
                      fontFamily: "monospace",
                      fontSize: "0.8rem",
                      maxWidth: 200,
                      wordBreak: "break-all",
                    }}
                  >
                    {row.address}
                  </TableCell>
                  <TableCell>{row.description}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="View details">
                      <IconButton
                        size="small"
                        aria-label="View details"
                        onClick={() => setSelectedContractId(row.id)}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {!rows.length && !contractsQuery.isPending && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography color="text.secondary">
                      No contracts match the current filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <ThirdPartyContractDetailsDialog
        open={detailsDialogOpen}
        contractId={selectedContractId}
        onClose={() => setSelectedContractId(null)}
      />
    </Box>
  );
}
